import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Input, Table, Modal, Badge, Space, Typography, List, Tag } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  display: block;
  margin-top: 8px;
`;

export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const TableCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    font-weight: 600;
  }

  .ant-table-tbody > tr {
    transition: background-color 0.2s;

    &:hover {
      background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const ActionButton = styled(Button)`
  background: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
  }

  &.ant-btn-dangerous {
    background: #ff4d4f !important;
    border-color: #ff4d4f !important;
    
    &:hover {
      background: #ff7875 !important;
      border-color: #ff7875 !important;
    }
  }
`;

export const PrimaryButton = styled(Button)`
  background: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
    box-shadow: 0 4px 12px rgba(45, 108, 202, 0.3);
  }
`;

export const DangerButton = styled(Button)`
  background: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
    transform: scale(1.05);
  }

  &.ant-btn-dangerous {
    background: #ff4d4f !important;
    
    &:hover {
      background: #ff7875 !important;
    }
  }
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

  &:hover {
    border-color: #40a9ff;
  }

  &:focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }

  .ant-modal-header {
    border-radius: 12px 12px 0 0;
    flex-shrink: 0;
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-modal-body {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    overflow-y: auto;
    max-height: calc(90vh - 200px);
    padding-right: 5px;
    padding-left: 5px;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')};
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${(props) => getThemeValue(props, '#434343', '#bfbfbf')};
      border-radius: 3px;
      
      &:hover {
        background: ${(props) => getThemeValue(props, '#595959', '#999999')};
      }
    }
  }

  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    border-radius: 0 0 12px 12px;
    flex-shrink: 0;
  }
`;

export const CategoryBadge = styled(Badge)`
  .ant-badge {
    border-radius: 16px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

export const EmptyStateText = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')} !important;
  display: block;
  margin-top: 8px;
`;

export const LoadingContainer = styled.div`
  padding: 48px;
  text-align: center;
`;

export const AddressText = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-top: 4px;
  
  svg {
    margin-right: 4px;
  }
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SelectedItemsContainer = styled.div`
  padding: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  border-radius: 8px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
  margin-top: 16px;
`;

export const SelectedItemsWrap = styled(Space)`
  margin-top: 12px;
  flex-wrap: wrap;
`;

export const InfoBox = styled.div`
  padding: 12px;
  background: ${(props) => getThemeValue(props, 'rgba(24, 144, 255, 0.1)', 'rgba(24, 144, 255, 0.05)')};
  border: 1px solid ${(props) => getThemeValue(props, 'rgba(24, 144, 255, 0.3)', 'rgba(24, 144, 255, 0.2)')};
  border-radius: 8px;
  margin-top: 16px;
`;

export const InfoText = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#58a6ff', '#1890ff')} !important;
`;

export const StyledTag = styled(Tag)`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
`;

export const DropdownContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

export const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  border-radius: 8px;
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.1)')};
  
  .ant-list {
    background: transparent;
    border-radius: 8px;
  }
  
  .ant-list-item {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#f0f0f0')} !important;
    padding: 12px 16px !important;
    
    &:last-child {
      border-bottom: none !important;
    }
  }
`;

export const SectionTitle = styled(Typography.Title)`
  margin-bottom: 12px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600 !important;
`;

export const SecondaryText = styled(Typography.Text)`
  display: block;
  margin-top: 8px;
`;

export const IconSpacer = styled.span`
  margin-right: 4px;
`;

export const ScheduleTimeText = styled.div`
  font-size: 12px;
  opacity: 0.7;
`;

export const ListItemContent = styled(Space)`
  width: 100%;
`;

export const ListItemSelected = styled(List.Item)<{ $isSelected: boolean }>`
  cursor: pointer;
  padding: 12px 16px !important;
  background-color: ${(props) => 
    props.$isSelected 
      ? getThemeValue(props, 'rgba(45, 108, 202, 0.15)', 'rgba(0, 0, 0, 0.05)')
      : 'transparent'
  } !important;
  transition: background-color 0.2s;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;

  &:hover {
    background-color: ${(props) => 
      props.$isSelected 
        ? getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(0, 0, 0, 0.08)')
        : getThemeValue(props, 'rgba(255, 255, 255, 0.05)', 'rgba(0, 0, 0, 0.04)')
    } !important;
  }
`;

export const LastUpdatedContainer = styled.div`
  text-align: right;
`;

export const LastUpdatedLabel = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 4px;
`;

export const LastUpdatedValue = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

export const TableWrapper = styled.div`
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  padding: 12px;
  border-radius: 12px;
`;

export const StyledButton = styled(Button)`
  background: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: white !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    border-color: #2560b8 !important;
    color: white !important;
  }
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  border-radius: 8px !important;
  color: #121212 !important;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 16px !important;
  font-weight: 500 !important;
  margin-left: 24px;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #d9d9d9 !important;
    color: #121212 !important;
    border: 0 !important;
  }

  &:focus {
    background-color: #ededed !important;
    color: #121212 !important;
    border: 0 !important;
  }
`;

export const ErrorText = styled(Typography.Text)`
  color: #ff4d4f !important;
  font-size: 12px;
  display: block;
  margin-top: 4px;
`;

export const FieldLabel = styled.label`
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
`;

export const TitleIcon = styled.span`
  margin-right: 12px;
`;

export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#eee')};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, 'transparent', 'inherit')};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || '#2D6CCA';
      if (iconColor === '#f5222d') {
        return getThemeValue(props, 'rgba(245, 34, 45, 0.2)', 'rgba(245, 34, 45, 0.1)');
      }
      return getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(45, 108, 202, 0.1)');
    }} !important;

    svg {
      color: ${(props) => props.iconColor || '#2D6CCA'} !important;
    }
  }
`;
